import React, { useRef, useState, useEffect } from "react";
import nextbillion, { NBMap, Marker, Popup } from "@nbai/nbmap-gl";
import "@nbai/nbmap-gl/dist/nextbillion.css";
import polyline from "@mapbox/polyline";

import rawData from "./locationsDataRaw";
import getAddressLatLong from "../utils/getAddressLatLong";

const NB_API_KEY = process.env.REACT_APP_NEXTBILLION_API_KEY;

export default function NextBillionDistance({
  distanceModal,
  setDistanceModal,
  locations,
  products,
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);

  const [selectedStores, setSelectedStores] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [locationsWithCoordinates, setLocationsWithCoordinates] = useState([]);

  const [routeStops, setRouteStops] = useState([]);
  const [checkedStoreIds, setCheckedStoreIds] = useState([]);

  const [loading, setLoading] = useState(false);

  const set = new Set();
  const buildRouteList = (route, selectedStores) => {
    return route.steps.map((step, idx) => {
      const store = selectedStores[step.location_index];

      return {
        order: idx + 1,
        name: store?.name ?? "Unknown",
        coords: store?.coords,
        arrivalMin: Math.round(step.arrival / 60),
        distanceKm: (step.distance / 1000).toFixed(1),
        durationMin: Math.round(step.duration / 60),
      };
    });
  };

  const handleStoreCheck = (storeId) => {
    setSelectedStores((prev) => {
      // prevent duplicate
      if (prev.some((s) => s.id === storeId)) return prev;

      const store = locationsWithCoordinates.find((loc) => loc.id === storeId);

      return store ? [...prev, store] : prev;
    });
  };
  const updateMarkers = (stores = []) => {
    if (!mapRef.current) {
      console.warn("⚠️ updateMarkers called but map not ready");
      return;
    }

    // Remove old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    stores.forEach((store, index) => {
      let label = "";

      if (index === 0) {
        label = "S"; // Start
      } else if (index === stores.length - 1) {
        label = "E"; // End
      } else {
        label = index.toString(); // Optimized order
      }

      // Create numbered marker element
      const el = document.createElement("div");
      el.className = "route-marker";
      el.innerText = label;

      Object.assign(el.style, {
        background:
          index === 0
            ? "#16a34a"
            : index === stores.length - 1
              ? "#dc2626"
              : "#2563eb",
        color: "#fff",
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        cursor: "pointer",
      });

      const marker = new Marker({ element: el })
        .setLngLat({
          lng: store.coords[0],
          lat: store.coords[1],
        })
        .addTo(mapRef.current);

      markersRef.current.push(marker);
    });
  };
  useEffect(() => {
    if (!locations || locations.length === 0) return;

    const fetchCoordinates = async () => {
      try {
        const results = await Promise.all(
          locations.map(async (loc) => {
            const { address, city, state, zipCode } = loc;
            const coords = await getAddressLatLong(
              address,
              city,
              state,
              zipCode,
            );
            return { ...loc, locationCoordinates: coords };
          }),
        );

        let set = new Set();

        setLocationsWithCoordinates(
          results
            .map((ele, index) => {
              const lat = ele?.locationCoordinates?.lat;
              const lng = ele?.locationCoordinates?.lng;

              let key = `${lat}_${lng}`;
              if (set.has(key)) return null;

              set.add(key);
              return {
                id: index + 1,
                name: ele.storeName,
                address: `${ele.address}, ${ele.city}, ${ele.state}, ${ele.zipCode}`,
                coords: [lng, lat], // ✔ change order only if needed
              };
            })
            .filter(Boolean),
        );
      } catch (error) {
        console.error("Error fetching coordinates:", error);
      }
    };

    fetchCoordinates();
  }, [locations]);

  useEffect(() => {
    if (!distanceModal || mapRef.current) return;

    nextbillion.setApiKey(NB_API_KEY);

    const nbmap = new NBMap({
      container: mapContainerRef.current,
      style: "https://api.nextbillion.io/maps/streets/style.json",
      center: { lng: 72.5, lat: 22.3 },
      zoom: 7,
    });

    nbmap.map.once("load", () => {
      mapRef.current = nbmap.map; // ✅ ALWAYS STORE THIS
      // updateMarkers();
    });
  }, [distanceModal]);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const fetchDirectionsRoute = async (orderedStores) => {
    fetchOptimizedOrder();
    const origin = `${orderedStores[0].coords[1]},${orderedStores[0].coords[0]}`;
    const destination = `${orderedStores[orderedStores.length - 1].coords[1]},${
      orderedStores[orderedStores.length - 1].coords[0]
    }`;
    const waypoints = orderedStores
      .slice(1, -1)
      .map((s) => `${s.coords[1]},${s.coords[0]}`)
      .join("|");

    const requestBody = {
      origin,
      destination,
      mode: "car",
      geometry: "geojson",
    };

    if (waypoints) requestBody.waypoints = waypoints;

    const res = await fetch(
      `https://api.nextbillion.io/directions/json?key=${NB_API_KEY}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      },
    );

    const data = await res.json();

    if (data.status !== "Ok" || !data.routes || !data.routes[0]) {
      throw new Error("No route found");
    }

    const route = data.routes[0];

    const geometry = route.geojson;

    const legs = route.legs.map((leg, i) => ({
      from: orderedStores[i].name,
      to: orderedStores[i + 1].name,
      distance: (leg.distance.value / 1000).toFixed(1),
      duration: (leg.duration.value / 60).toFixed(0),
    }));

    return {
      orderedStores,
      geometry,
      legs,
      totalDistance: (route.distance / 1000).toFixed(1),
      totalDuration: (route.duration / 60).toFixed(0),
      totalDurationHours: (route.duration / 3600).toFixed(1),
    };
  };

  const addStoreToRoute = (storeId) => {
    const store = locationsWithCoordinates.find((loc) => loc.id === storeId);
    if (store && !selectedStores.find((s) => s.id === storeId)) {
      setSelectedStores([...selectedStores, store]);
    }
  };

  const removeStoreFromRoute = (storeId) => {
    setSelectedStores(selectedStores.filter((s) => s.id !== storeId));
    setRouteInfo(null);
  };

  const moveStoreUp = (index) => {
    if (index === 0) return;
    const newOrder = [...selectedStores];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    setSelectedStores(newOrder);
    setRouteInfo(null);
  };

  const moveStoreDown = (index) => {
    if (index === selectedStores.length - 1) return;
    const newOrder = [...selectedStores];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    setSelectedStores(newOrder);
    setRouteInfo(null);
  };

  const drawRoute = (geojson) => {
    if (!mapRef.current) {
      console.warn("⚠️ drawRoute called but map is not ready");
      return;
    }

    const map = mapRef.current;

    if (map.getSource("route")) {
      map.getSource("route").setData(geojson);
    } else {
      map.addSource("route", {
        type: "geojson",
        data: geojson,
      });

      map.addLayer({
        id: "route-layer",
        type: "line",
        source: "route",
        paint: {
          "line-width": 4,
          "line-color": "#1e90ff",
        },
      });
    }
  };

  const clearRoute = () => {
    setSelectedStores([]);
    setRouteInfo(null);
    updateMarkers();

    if (mapRef.current) {
      try {
        if (mapRef.current.getLayer("route")) {
          mapRef.current.removeLayer("route");
        }
        if (mapRef.current.getSource("route")) {
          mapRef.current.removeSource("route");
        }
      } catch (e) {
        console.error("Clear error:", e);
      }
    }
  };

  const handleClose = () => {
    clearRoute();

    // Remove all markers
    markersRef.current.forEach((marker) => {
      if (marker && marker.remove) marker.remove();
    });
    markersRef.current = [];

    if (mapRef.current) {
      try {
        // Remove layers and sources
        if (mapRef.current.getLayer && mapRef.current.getLayer("route")) {
          mapRef.current.removeLayer("route");
        }
        if (mapRef.current.getSource && mapRef.current.getSource("route")) {
          mapRef.current.removeSource("route");
        }

        // Properly remove the map
        if (
          mapRef.current.remove &&
          typeof mapRef.current.remove === "function"
        ) {
          mapRef.current.remove();
        }
      } catch (error) {
        console.error("Error removing map:", error);
      }
      mapRef.current = null;
    }
    setDistanceModal(false);
  };
  const getOptimizedResult = async (jobId) => {
    if (!jobId) throw new Error("Missing optimization jobId");

    while (true) {
      const res = await fetch(
        `https://api.nextbillion.io/optimization/v2/result?id=${jobId}&key=${NB_API_KEY}`,
      );

      const data = await res.json();

      if (data.message === "Job still processing") {
        await sleep(10000);
        continue;
      }

      if (data.status === "Ok" && data.result?.routes?.length) {
        return data;
      }

      console.error("Optimization failed:", data);
      throw new Error("Optimization failed");
    }
  };

  const fetchOptimizedOrder = async (stores = []) => {
    if (!Array.isArray(stores) || stores.length < 2) {
      throw new Error("Please select at least 2 locations");
    }

    // Convert coords → "lat,lng"
    const locationStrings = stores.map((s) => `${s.coords[1]},${s.coords[0]}`);

    const payload = {
      locations: {
        id: 1,
        location: locationStrings,
      },
      vehicles: [
        {
          id: "vehicle-1",
          start_index: 0, // first location = start
        },
      ],
      jobs: locationStrings.slice(1).map((_, i) => ({
        id: `job-${i + 1}`,
        location_index: i + 1,
      })),
    };

    const res = await fetch(
      `https://api.nextbillion.io/optimization/v2?key=${NB_API_KEY}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    const data = await res.json();

    if (!data?.id) {
      console.error("Job creation failed:", data);
      throw new Error("Failed to create optimization job");
    }

    return getOptimizedResult(data.id);
  };
  const formatMinutesToHours = (minutes) => {
    if (!minutes && minutes !== 0) return "--";

    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} hr`;

    return `${hrs} hr ${mins} min`;
  };

  const waitForMap = async () => {
    while (!mapRef.current) {
      await new Promise((r) => setTimeout(r, 300));
    }
  };

  const applyOptimizedRoute = async () => {
    if (!selectedStores || selectedStores.length < 2) {
      console.warn("⚠️ selectedStores invalid:", selectedStores);
      alert("Please select at least 2 locations");
      return;
    }

    setLoading(true);
    setRouteInfo(null);

    try {
      await waitForMap();

      if (!mapRef.current) {
        throw new Error("❌ Map still undefined after waitForMap()");
      }

      // ===============================
      // 3️⃣ CALL OPTIMIZATION
      // ===============================
      const data = await fetchOptimizedOrder(selectedStores);

      // ===============================
      // 4️⃣ ROUTE CHECK
      // ===============================
      if (!data?.result?.routes?.length) {
        throw new Error("❌ No routes returned from optimization");
      }

      const route = data.result.routes[0];

      // ===============================
      // 5️⃣ STEPS CHECK
      // ===============================
      if (!Array.isArray(route.steps)) {
        throw new Error("❌ route.steps is undefined");
      }

      // ===============================
      // 6️⃣ ORDERED STORES
      // ===============================
      const orderedStores = route.steps
        .map((s) => {
          return selectedStores[s.location_index];
        })
        .filter(Boolean);

      if (!orderedStores.length) {
        throw new Error("❌ orderedStores is empty");
      }

      // ===============================
      // 7️⃣ UPDATE MARKERS
      // ===============================
      updateMarkers(orderedStores);

      // ===============================
      // 8️⃣ GEOMETRY CHECK
      // ===============================
      if (!route.geometry) {
        throw new Error("❌ route.geometry is missing");
      }

      const decoded = polyline.decode(route.geometry);

      // ===============================
      // 9️⃣ DRAW ROUTE
      // ===============================
      const geojson = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: decoded.map(([lat, lng]) => [lng, lat]),
        },
      };

      drawRoute(geojson);

      const stops = buildRouteList(route, selectedStores);
      const n = stops.length - 1;
      const newStops = stops.slice(0, n);
      setRouteStops(newStops);

      // ===============================
      // 🔟 ROUTE INFO
      // ===============================
      // setRouteInfo({
      //   route: orderedStores?.map((s) => s.name),
      //   totalDistance: (route.distance / 1000)?.toFixed(1),
      //   totalDurationHours: (route.duration / 3600)?.toFixed(1),
      // });
    } catch (err) {
      console.error("🔥 ERROR in applyOptimizedRoute:", err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllLoc = () => {
    setSelectedStores((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const newStores = locationsWithCoordinates.filter(
        (s) => !existingIds.has(s.id),
      );

      return [...prev, ...newStores];
    });
  };

  const makeStartPoint = (index) => {
    setSelectedStores((prev) => {
      const updated = [...prev];
      const [selected] = updated.splice(index, 1); // remove clicked
      updated.unshift(selected); // move to first
      return updated;
    });
  };

  if (!distanceModal) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "95vw",
          height: "90vh",
          backgroundColor: "#fff",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
        }}
      >
        <div
          style={{
            width: "450px",
            minWidth: "450px",
            padding: "20px",
            overflowY: "auto",
            borderRight: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2 style={{ fontSize: "22px", fontWeight: "bold", margin: 0 }}>
              Custom Route Planner
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "28px",
                cursor: "pointer",
                padding: "0",
                lineHeight: "1",
              }}
            >
              ×
            </button>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <h3
              style={{
                fontWeight: "600",
                marginBottom: "10px",
                fontSize: "15px",
                color: "#2563eb",
              }}
            >
              ➕ Add Store to Route
            </h3>

            <p
              className="cursor-pointer m-2 border-2 border-blue-500 border rounded-md w-[140px] bg-blue-400 text-white p-1"
              onClick={handleSelectAllLoc}
            >
              Select All Locations
            </p>
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: "2px solid #2563eb",
                borderRadius: "8px",
                padding: "8px",
              }}
            >
              {locationsWithCoordinates.length === selectedStores.length ? (
                <p style={{ fontSize: "13px", color: "#6b7280" }}>
                  All stores added
                </p>
              ) : (
                locationsWithCoordinates
                  .filter((loc) => !selectedStores.find((s) => s.id === loc.id))
                  ?.map((loc) => (
                    <label
                      key={loc.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={() => handleStoreCheck(loc.id)}
                      />
                      {loc.name}
                    </label>
                  ))
              )}
            </div>
          </div>

          {selectedStores.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <h3
                style={{
                  fontWeight: "600",
                  marginBottom: "10px",
                  fontSize: "15px",
                  color: "#15803d",
                }}
              >
                🗺️ Your Route ({selectedStores.length} stores)
              </h3>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                {selectedStores.map((store, index) => (
                  <div
                    key={store.id}
                    onClick={() => makeStartPoint(index)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      // backgroundColor: "#fff",
                      padding: "10px",
                      marginBottom: "6px",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div>
                      {index === 0 && (
                        <span
                          style={{
                            fontSize: "10px",
                            color: "#22c55e",
                            fontWeight: "600",
                          }}
                        >
                          START
                        </span>
                      )}
                      <span
                        style={{
                          width: "28px",
                          height: "28px",
                          backgroundColor:
                            index === 0
                              ? "#22c55e"
                              : index === selectedStores.length - 1
                                ? "#ef4444"
                                : "#f59e0b",
                          color: "#fff",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "bold",
                          marginRight: "10px",
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </span>
                    </div>

                    <div style={{ flex: 1, fontSize: "13px" }}>
                      <div style={{ fontWeight: "600" }}>{store.name}</div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        {store.address}
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", gap: "4px", marginLeft: "8px" }}
                    >
                      {/* <button
                        // onClick={() => moveStoreUp(index)}
                        disabled={index === 0}
                        style={{
                          padding: "4px 8px",
                          fontSize: "14px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          cursor: index === 0 ? "not-allowed" : "pointer",
                          opacity: index === 0 ? 0.5 : 1,
                        }}
                      >
                        ↑
                      </button> */}
                      {/* <button
                        // onClick={() => moveStoreDown(index)}
                        disabled={index === selectedStores.length - 1}
                        style={{
                          padding: "4px 8px",
                          fontSize: "14px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          cursor:
                            index === selectedStores.length - 1
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            index === selectedStores.length - 1 ? 0.5 : 1,
                        }}
                      >
                        ↓
                      </button> */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // ⛔ stop parent click
                          removeStoreFromRoute(store.id);
                        }}
                        style={{
                          padding: "4px 8px",
                          fontSize: "14px",
                          border: "1px solid #ef4444",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#ef4444",
                          cursor: "pointer",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={applyOptimizedRoute}
            disabled={selectedStores.length < 2 || loading}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "8px",
              border: "none",
              backgroundColor:
                selectedStores.length < 2 || loading ? "#d1d5db" : "#2563eb",
              color: "#fff",
              fontSize: "15px",
              fontWeight: "600",
              cursor:
                selectedStores.length < 2 || loading
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.2s",
              boxShadow:
                selectedStores.length < 2 || loading
                  ? "none"
                  : "0 4px 12px rgba(37, 99, 235, 0.3)",
            }}
          >
            {loading
              ? "⏳ Calculating..."
              : `🚀 Calculate Route (${selectedStores.length} stores)`}
          </button>
          <div className="p-4 bg-slate-50 border-l border-slate-200  h-[300px] my-2 overflow-scroll">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              🛣️ Optimized Route
            </h3>

            {routeStops.length === 0 && (
              <p className="text-sm text-slate-500">No route generated</p>
            )}

            <ul className="relative space-y-4">
              {routeStops.map((stop, index) => {
                const isStart = index === 0;
                const isEnd = index === routeStops.length - 1;

                return (
                  <li
                    key={stop.order}
                    className="relative flex gap-4 items-center"
                    onClick={() => {
                      mapRef.current?.flyTo({
                        center: stop.coords,
                        zoom: 13,
                      });
                    }}
                  >
                    {/* Vertical route line */}
                    {!isEnd && (
                      <div className="absolute left-4 top-10 h-full w-[2px] bg-gradient-to-b from-blue-400 to-slate-200" />
                    )}

                    {/* Stop marker */}
                    <div
                      className={`relative z-10 w-8 h-8 mt-2 rounded-full flex items-center justify-center text-xs font-bold text-white
            transition-all duration-300
            ${
              isStart
                ? "bg-green-500 scale-110 shadow-lg"
                : isEnd
                  ? "bg-red-500 scale-110 shadow-lg"
                  : "bg-blue-600"
            }
          `}
                    >
                      {isStart ? "S" : isEnd ? "E" : stop.order - 1}
                    </div>

                    {/* Card */}
                    <div
                      className={`
            flex-1 p-4 rounded-xl border
            bg-white
            shadow-sm
            transition-all duration-300
            hover:shadow-md hover:translate-x-1
            cursor-pointer
            ${
              isStart
                ? "border-green-300 bg-green-50"
                : isEnd
                  ? "border-red-300 bg-red-50"
                  : "border-slate-200"
            }
          `}
                    >
                      <div className="flex justify-between items-center">
                        <div className="font-semibold text-slate-800">
                          {stop.name}
                        </div>

                        {isStart && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                            Start
                          </span>
                        )}

                        {isEnd && (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                            End
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-slate-500 mt-2 flex gap-2">
                        <span className="flex items-center border border-1 rounded-sm  gap-1">
                          ⏱ {formatMinutesToHours(stop.arrivalMin)}
                        </span>

                        <span className="flex items-center  border border-1 rounded-sm   gap-1">
                          📏{" "}
                          {index === 0
                            ? "0 km"
                            : `${(
                                stop.distanceKm -
                                routeStops[index - 1].distanceKm
                              ).toFixed(2)} km`}
                        </span>
                        <span className="flex items-center  border border-1 rounded-sm px-1 gap-1">
                          <strong>From Start </strong>:{stop.distanceKm}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            position: "relative",
            minHeight: "100%",
            minWidth: 0,
          }}
        >
          <div
            ref={mapContainerRef}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
}
